import React from "react"
import { useTranslation } from "react-i18next"

import {
  sendDeviceTestNotificationAsync,
  sendFarmTestNotificationAsync,
} from "@/base/farmhq-api"
import { useRequestHelpers } from "@/hooks/useRequestHelpers"
import { AlertDialog, AlertDialogBody, AppText } from "@/theme"

import { ActionButtons } from "./ActionButtons"

interface Props {
  /**
   * When provided the test is scoped to this device and goes to everyone
   * configured for it. When omitted the test goes to every member of the
   * active farm.
   */
  deviceId?: string
  onClose: () => void
}

/**
 * Confirmation before sending a test notification to other people.
 *
 * Both scopes message users other than the sender, so both are confirmed.
 * The self-test has no dialog because it can only reach the sender.
 */
export function SendTestNotificationDialog({ deviceId, onClose }: Props) {
  const { t } = useTranslation("deviceNotifications")
  const isDeviceScoped = deviceId != null
  const { dispatch, handleError, isLoading, toasts } = useRequestHelpers(
    isDeviceScoped ? "SendDeviceTestNotification" : "SendFarmTestNotification",
  )

  const onPressSubmit = () => {
    // Dispatched in separate branches rather than as one union-typed thunk:
    // TS cannot resolve the dispatch overload for a union of thunk types.
    const request =
      deviceId == null
        ? dispatch(sendFarmTestNotificationAsync(undefined)).unwrap()
        : dispatch(sendDeviceTestNotificationAsync({ deviceId })).unwrap()

    request
      .then((result) => {
        toasts.success(
          t("testNotificationSent", { count: result.nRecipients }),
        )
        return onClose()
      })
      .catch((error) => {
        handleError(error, { toastMessage: "default" })
      })
  }

  return (
    <AlertDialog
      isOpen
      id={"confirmSendTestNotification"}
      titleElement={t("sendTestNotificationQuestion")}
      actionElement={
        <ActionButtons
          buttonSize={"sm"}
          isLoading={isLoading}
          orientation={"horizontal"}
          submitText={t("sendTestNotification")}
          onPressCancel={onClose}
          onPressSubmit={onPressSubmit}
        />
      }
      onClose={onClose}
    >
      <AlertDialogBody>
        <AppText>
          {isDeviceScoped
            ? t("testNotificationDeviceWarning")
            : t("testNotificationFarmWarning")}
        </AppText>
      </AlertDialogBody>
    </AlertDialog>
  )
}
